import type { ReactNode } from 'react';
import { useToggle } from '../../../util/toggle';
import { HorizontalStack, type HorizontalStackProps } from '../../alignment/horizontal-stack';
import { TextButton, type TextButtonSize } from '../../link/text-link/text-button';
import { Typography, type TypographyProps } from '../../typography';

function defaultMaskFn(value: string) {
  return value.replace(/./g, 'X');
}

export function maskSSN(value: string) {
  return value.replace(/(\d{3})-(\d{2})-(\d{4})/, 'XXX-XX-$3');
}

export function maskITIN(value: string) {
  return value.replace(/(^9)(\d{2})-(\d{2})-(\d{4})/, '$1XX-XX-$4');
}

export function maskEIN(value: string) {
  return value.replace(/(\d{2})-(\d{7})/, 'XX-$2');
}

export interface SensitiveValueFormatProps extends HorizontalStackProps {
  buttonSize?: TextButtonSize;
  hideChildren?: ReactNode;
  maskFn?: (value: string) => string;
  showChildren?: ReactNode;
  value: string | undefined;
  valueTypographyProps?: TypographyProps;
}

export function SensitiveValueFormat({
  buttonSize = 'small',
  hideChildren = 'Hide',
  maskFn = defaultMaskFn,
  showChildren = 'Show',
  value,
  valueTypographyProps,
  ...rest
}: SensitiveValueFormatProps) {
  const [isVisible, toggleIsVisible] = useToggle();

  if (!value) {
    return null;
  }

  return (
    <HorizontalStack blockAlign="center" gap={2} wrap="nowrap" {...rest}>
      <Typography {...valueTypographyProps}>{isVisible ? value : maskFn(value)}</Typography>

      <TextButton onClick={toggleIsVisible} size={buttonSize}>
        {isVisible ? hideChildren : showChildren}
      </TextButton>
    </HorizontalStack>
  );
}
